import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api, unwrap } from '../api/client';
import { useEmployeeOptions, useLocationOptions } from '../api/queries';
import type { Asset } from '../api/types';
import { cn } from '../lib/cn';
import { StatusBadge } from './ui';
import { Input, Select } from './ui/form';

type SelectProps = {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  emptyLabel?: string;
  disabled?: boolean;
  required?: boolean;
  includeInactive?: boolean;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
};

/** Mitarbeiter-Auswahl; Wert = Kürzel. */
export function EmployeeSelect({ value, onChange, emptyLabel = '– keiner –', includeInactive, ...rest }: SelectProps) {
  const q = useEmployeeOptions(includeInactive);
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} {...rest}>
      <option value="">{q.isPending ? 'Lädt …' : emptyLabel}</option>
      {q.data?.map((e) => (
        <option key={e.id} value={e.short_code}>
          {e.short_code} – {e.name}
          {e.is_active ? '' : ' (inaktiv)'}
        </option>
      ))}
    </Select>
  );
}

/** Standort-Auswahl; Wert = Standort-ID. */
export function LocationSelect({ value, onChange, emptyLabel = '– keiner –', includeInactive, ...rest }: SelectProps) {
  const q = useLocationOptions(includeInactive);
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} {...rest}>
      <option value="">{q.isPending ? 'Lädt …' : emptyLabel}</option>
      {q.data?.map((l) => (
        <option key={l.id} value={l.id}>
          {l.name}
          {l.code ? ` (${l.code})` : ''}
          {l.is_active ? '' : ' (inaktiv)'}
        </option>
      ))}
    </Select>
  );
}

/** Asset suchen und auswählen (Inventarnummer, Seriennummer, Name …). */
export function AssetPicker({
  id,
  value,
  onChange,
  exclude = [],
  filter,
  placeholder = 'Inventarnummer, Seriennummer oder Name …',
}: {
  id?: string;
  value: Asset | null;
  onChange: (asset: Asset | null) => void;
  exclude?: string[];
  filter?: (a: Asset) => boolean;
  placeholder?: string;
}) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 250);
    return () => clearTimeout(t);
  }, [term]);

  const q = useQuery({
    queryKey: ['assets', 'picker', debounced],
    queryFn: () => unwrap(api.GET('/api/assets', { params: { query: { q: debounced, page_size: 15, status: 'active,in_stock,repair,retired,lost,unknown' } } })),
    enabled: debounced.length >= 2,
  });
  const results = (q.data?.data ?? []).filter((a) => !exclude.includes(a.id) && (!filter || filter(a)));

  if (value) {
    return (
      <div className="flex h-9 items-center justify-between gap-2 rounded-md border border-slate-300 bg-slate-50 px-2.5 text-sm">
        <span className="truncate">
          <span className="font-mono font-medium">{value.asset_number}</span> – {value.name}
        </span>
        <button type="button" onClick={() => onChange(null)} className="text-slate-400 hover:text-slate-700" aria-label="Auswahl entfernen">
          <X className="size-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <Search className="pointer-events-none absolute top-2.5 left-2.5 size-4 text-slate-400" />
      <Input
        id={id}
        className="pl-8"
        value={term}
        placeholder={placeholder}
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        autoComplete="off"
      />
      {open && debounced.length >= 2 && (
        <div className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg">
          {q.isPending && <div className="px-3 py-2 text-sm text-slate-500">Suche …</div>}
          {!q.isPending && results.length === 0 && <div className="px-3 py-2 text-sm text-slate-500">Keine passenden Assets</div>}
          {results.map((a) => (
            <button
              key={a.id}
              type="button"
              className={cn('flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50')}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(a);
                setTerm('');
                setOpen(false);
              }}
            >
              <span className="min-w-0">
                <span className="font-mono font-medium">{a.asset_number}</span> – {a.name}
                <span className="block truncate text-xs text-slate-500">
                  {a.asset_type.label}
                  {a.serial_number ? ` · SN ${a.serial_number}` : ''}
                  {a.parent ? ` · eingebaut in ${a.parent.asset_number}` : ''}
                </span>
              </span>
              <StatusBadge status={a.status} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
